import { useEffect, useRef } from "react";

import useTranslation from "src/translations/useTranslation";
import { MapMessages } from "@shared/languages/interfaces";

/** Texts of the full screen button, in the user's language */
export interface MapFullscreenLabels {
  enter: string;
  exit: string;
}

/** Full screen button placed on a map */
export interface MapFullscreen {
  /** Change the button texts (the user switched language) */
  setLabels: (labels: MapFullscreenLabels) => void;
  /** Leave full screen, give the page back as it was and take the button off the map */
  destroy: () => void;
}

/** Fullscreen API with the prefixes older Safari (and iPad) still use */
interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}
interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
  showPopover?: () => void;
  hidePopover?: () => void;
}

// Material icons "fullscreen" and "fullscreen_exit", the same ones the travel map uses
const ENTER_ICON = "M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z";
const EXIT_ICON = "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z";

// Time the browser has to enter its full screen before the map takes the whole window instead
export const NATIVE_FULLSCREEN_TIMEOUT_MS = 1000;

// The map over the whole window, above dialogs (1300), like the travel map does where full screen is refused
const WINDOW_STYLES: [string, string][] = [
  ["position", "fixed"], ["top", "0"], ["right", "0"], ["bottom", "0"], ["left", "0"],
  ["width", "100%"], ["height", "100%"], ["max-width", "none"], ["max-height", "none"],
  ["margin", "0"], ["padding", "0"], ["border", "0"], ["overflow", "hidden"], ["z-index", "1400"]
];

/**
 * Put a full screen button on a map, where Google's own button sits (top right) and with its look.
 *
 * Google hides its button on iPhones, which have no Fullscreen API. This one asks the browser for full screen
 * first; where the browser has no full screen, refuses it or ignores it, the map takes the whole window instead.
 * Escape leaves the window mode before it reaches anything else (an open dialog stays open).
 * @param map Map that gets the button
 * @param labels Button texts
 * @return The button, to change its texts and to take it off
 */
export const attachMapFullscreen = (map: google.maps.Map, labels: MapFullscreenLabels): MapFullscreen => {

  const container = map.getDiv() as FullscreenElement;
  const fullscreenDocument = document as FullscreenDocument;
  const controls = map.controls[google.maps.ControlPosition.TOP_RIGHT];
  const button = document.createElement("button");

  let currentLabels = labels;
  let mode: "native" | "window" | null = null;
  let fallbackTimer: number | undefined;
  // What the window mode changed (the map's own inline styles and gestures), to give it back as it was
  let windowRestore: {
    styles: [string, string, string][],
    gestureHandling?: google.maps.MapOptions["gestureHandling"],
    popover: boolean
  } | null = null;

  button.type = "button";
  button.className = "map-fullscreen-control";
  button.style.cssText = "display:flex;align-items:center;justify-content:center;width:40px;height:40px;margin:10px;padding:0;"
    + "border:0;border-radius:2px;background:#FFFFFF;box-shadow:rgba(0,0,0,0.3) 0 1px 4px -1px;color:#666666;cursor:pointer;";

  const render = () => {
    const label = mode ? currentLabels.exit : currentLabels.enter;

    button.title = label;
    button.setAttribute("aria-label", label);
    button.innerHTML = "<svg width='28' height='28' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>"
      + `<path d='${mode ? EXIT_ICON : ENTER_ICON}'/></svg>`;
  };

  const nativeElement = () => fullscreenDocument.fullscreenElement || fullscreenDocument.webkitFullscreenElement || null;

  const clearFallback = () => {
    if (fallbackTimer === undefined) return;
    window.clearTimeout(fallbackTimer);
    fallbackTimer = undefined;
  };

  const enterWindow = () => {
    if (mode) return;

    windowRestore = {
      styles: WINDOW_STYLES.map(([property]): [string, string, string] => [
        property, container.style.getPropertyValue(property), container.style.getPropertyPriority(property)
      ]),
      gestureHandling: map.get("gestureHandling"),
      popover: false
    };

    WINDOW_STYLES.forEach(([property, value]) => container.style.setProperty(property, value, "important"));
    // One finger moves the map (no "use two fingers" on phones), and the page behind does not scroll under it
    map.setOptions({ gestureHandling: "greedy" });

    // In the top layer nothing on the page (header, a column with its own layer) can cover the map
    if (typeof container.showPopover === "function") {
      try {
        container.setAttribute("popover", "manual");
        container.showPopover();
        windowRestore.popover = true;
      } catch (error) {
        container.removeAttribute("popover");
      }
    }

    mode = "window";
    render();
  };

  const leaveWindow = () => {
    if (mode !== "window" || !windowRestore) return;

    if (windowRestore.popover) {
      try {
        container.hidePopover?.();
      } catch (error) {
        // Already hidden (the map left the page)
      }
      container.removeAttribute("popover");
    }
    windowRestore.styles.forEach(([property, value, priority]) => {
      if (value) container.style.setProperty(property, value, priority);
      else container.style.removeProperty(property);
    });
    map.setOptions({ gestureHandling: windowRestore.gestureHandling || "auto" });

    windowRestore = null;
    mode = null;
    render();
  };

  const exitNative = () => {
    const exit = fullscreenDocument.exitFullscreen || fullscreenDocument.webkitExitFullscreen;

    try {
      Promise.resolve(exit?.call(fullscreenDocument)).catch(() => undefined);
    } catch (error) {
      // Already out of full screen
    }
  };

  // Whatever is not in full screen when the browser answers (or after the timeout) takes the whole window
  const fallback = () => {
    clearFallback();
    if (nativeElement() !== container) enterWindow();
  };

  const toggle = () => {
    if (mode === "native") {
      exitNative();

      return;
    }
    if (mode === "window") {
      leaveWindow();

      return;
    }

    const request = container.requestFullscreen || container.webkitRequestFullscreen;

    if (!request) {
      enterWindow();

      return;
    }

    clearFallback();
    fallbackTimer = window.setTimeout(fallback, NATIVE_FULLSCREEN_TIMEOUT_MS);
    try {
      Promise.resolve(request.call(container)).catch(fallback);
    } catch (error) {
      fallback();
    }
  };

  const onFullscreenChange = () => {
    if (nativeElement() === container) {
      clearFallback();
      leaveWindow();
      mode = "native";
    } else if (mode === "native") mode = null;
    render();
  };

  // Capture phase on the document: Escape leaves the window mode and goes no further (a dialog behind stays open)
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || mode !== "window") return;
    event.stopPropagation();
    leaveWindow();
  };

  button.addEventListener("click", toggle);
  document.addEventListener("fullscreenchange", onFullscreenChange);
  document.addEventListener("webkitfullscreenchange", onFullscreenChange);
  document.addEventListener("keydown", onKeyDown, true);
  render();
  controls.push(button);

  return {
    setLabels: (next) => {
      currentLabels = next;
      render();
    },
    destroy: () => {
      clearFallback();
      button.removeEventListener("click", toggle);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      document.removeEventListener("keydown", onKeyDown, true);
      if (mode === "native") {
        exitNative();
        mode = null;
      }
      leaveWindow();

      const index = controls.getArray().indexOf(button);

      if (index >= 0) controls.removeAt(index);
    }
  };
};

/**
 * Full screen button of a map: replaces Google's own, which iPhones never show.
 * @param map Map instance returned by useGoogleMaps (undefined until the API loads)
 * @param enabled False where the page draws its own full screen (Google's button stays hidden as well)
 */
export const useMapFullscreen = (map: google.maps.Map | undefined, enabled = true): void => {

  const { t } = useTranslation();
  const enter = t(MapMessages.fullscreen);
  const exit = t(MapMessages.exitFullscreen);
  const labelsRef = useRef<MapFullscreenLabels>({ enter, exit });
  const fullscreenRef = useRef<MapFullscreen>();

  labelsRef.current = { enter, exit };

  useEffect(() => {

    if (!map) return undefined;

    // The app has no error boundary: an error thrown here would blank the whole page, so the button never throws
    try {
      map.setOptions({ fullscreenControl: false });
      if (enabled) fullscreenRef.current = attachMapFullscreen(map, labelsRef.current);
    } catch (error) {
      console.log(error);
    }

    return () => {
      try {
        fullscreenRef.current?.destroy();
      } catch (error) {
        console.log(error);
      }
      fullscreenRef.current = undefined;
    };

  }, [map, enabled]);

  useEffect(() => {
    fullscreenRef.current?.setLabels({ enter, exit });
  }, [enter, exit]);
};
